import { UploadVideoCaptionResponse } from "../types";
import { getApiUrl, API_BASE_URL } from '../config/api';
import { handleAPIError } from './apiErrorHandler';

// audioApi.ts
interface UploadedFile {
  url: string;
  name: string;
}

// Chunked upload interfaces
interface ChunkedUploadInitResponse {
  sessionId: string;
  message: string;
}

interface ChunkUploadResponse {
  chunkIndex: number;
  success: boolean;
  sessionId: string;
  message?: string;
}

interface ChunkedUploadFinalizeResponse {
  success: boolean;
  jobId: string;
  sessionId: string;
  message: string;
  filePath: string;
  projectId: string;
}

interface ChunkedUploadStatusResponse {
  sessionId: string;
  status: 'uploading' | 'assembling' | 'completed' | 'failed';
  totalChunks: number;
  uploadedChunks: number[];
  message?: string;
  error?: string;
}


/**
 * Uploads an audio blob to the backend and returns the URL of the uploaded audio.
 *
 * @param videoBlob - The audio file as a Blob.
 * @returns A promise that resolves to the URL string if successful, or null if an error occurs.
 */
export async function uploadCaptionVideo(videoBlob: Blob):Promise<UploadVideoCaptionResponse> {
    // Prepare the form data
    const formData = new FormData();
    console.log("formData--> ", typeof(videoBlob));
    formData.append('file', videoBlob); // Adjust file name and MIME type as needed
  
    try {
      // Get authentication token
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Replace with your actual backend URL
      const response = await fetch(getApiUrl('/video/upload-caption-video'), {
        method: 'POST',
        headers,
        body: formData,
      });
  
      if (!response.ok) {
        await handleAPIError(response);
      }
  
      // Expect the backend to return a JSON object with a property like { url: 'https://...' }
      const data = await response.json();
      console.log("video api reponse --> ", data );
      return data as Promise<UploadVideoCaptionResponse> ;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

  export async function uploadCaptionVideoWithProgress(videoBlob: Blob):Promise<{
    jobId: string,
    projectId: string, 
    message: string }> {
    // Prepare the form data
    const formData = new FormData();
    console.log("formData--> ", typeof(videoBlob));
    formData.append('file', videoBlob); // Adjust file name and MIME type as needed
  
    try {
      // Get authentication token
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Replace with your actual backend URL
      const response = await fetch(getApiUrl('/video/upload-video-for-caption-with-progress'), {
        method: 'POST',
        headers,
        body: formData,
      });
  
      if (!response.ok) {
        throw new Error('Failed to upload video');
      }
  
      // Expect the backend to return a JSON object with a property like { url: 'https://...' }
      const data = await response.json();
      console.log("video api reponse --> ", data );
      return data as Promise<{
        jobId: string, 
        projectId: string,
        message: string }> ;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

// ============================================================================
// CHUNKED UPLOAD FUNCTIONS - For Large Files (6GB+)
// ============================================================================

/**
 * Initialize a chunked upload session for large video files.
 * 
 * @param fileName - Name of the file being uploaded
 * @param fileSize - Total size of the file in bytes
 * @param fileType - MIME type of the file
 * @param totalChunks - Total number of chunks the file will be split into
 * @param chunkSize - Size of each chunk in bytes (default 50MB)
 * @returns Promise with session information
 */
export async function initializeChunkedUpload(
  fileName: string, 
  fileSize: number, 
  fileType: string,
  totalChunks: number,
  chunkSize: number = 50 * 1024 * 1024
): Promise<ChunkedUploadInitResponse> {
  try {
    // Get authentication token
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(getApiUrl('/video/chunked/init-session'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        fileName,
        fileSize,
        fileType,
        chunkSize,
        totalChunks
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to initialize chunked upload: ${response.status}`);
    }

    const data = await response.json();
    console.log("Chunked upload session initialized:", data);
    return data;
  } catch (error) {
    console.error('Error initializing chunked upload:', error);
    throw error;
  }
}

/**
 * Upload a single chunk of the file.
 * 
 * @param sessionId - The upload session ID
 * @param chunkIndex - Zero-based index of this chunk
 * @param chunkBlob - The chunk data as a Blob
 * @param chunkHash - Optional MD5 hash of the chunk for validation
 * @returns Promise with chunk upload result
 */
export async function uploadVideoChunk(
  sessionId: string, 
  chunkIndex: number, 
  chunkBlob: Blob,
  chunkHash?: string
): Promise<ChunkUploadResponse> {
  const formData = new FormData();
  formData.append('sessionId', sessionId);
  formData.append('chunkIndex', chunkIndex.toString());
  formData.append('chunk', chunkBlob);
  
  if (chunkHash) {
    formData.append('chunkHash', chunkHash);
  }

  try {
    // Get authentication token
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {};
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(getApiUrl('/video/chunked/upload-chunk'), {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload chunk ${chunkIndex}: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Chunk ${chunkIndex} uploaded successfully:`, data);
    return data;
  } catch (error) {
    console.error(`Error uploading chunk ${chunkIndex}:`, error);
    throw error;
  }
}

/**
 * Finalize the chunked upload and trigger file assembly.
 * 
 * @param sessionId - The upload session ID
 * @returns Promise with job information for tracking progress
 */
export async function finalizeChunkedUpload(sessionId: string): Promise<ChunkedUploadFinalizeResponse> {
  try {
    // Get authentication token
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(getApiUrl('/video/chunked/finalize'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ sessionId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to finalize chunked upload: ${response.status}`);
    }

    const data = await response.json();
    console.log("Chunked upload finalized:", data);
    return data;
  } catch (error) {
    console.error('Error finalizing chunked upload:', error);
    throw error;
  }
}

/**
 * Check the status of a chunked upload session.
 * 
 * @param sessionId - The upload session ID
 * @returns Promise with session status information
 */
export async function getChunkedUploadStatus(sessionId: string): Promise<ChunkedUploadStatusResponse> {
  try {
    // Get authentication token
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(getApiUrl(`/video/chunked/status/${sessionId}`), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to get chunked upload status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting chunked upload status:', error);
    throw error;
  }
}

/**
 * Cancel a chunked upload session and clean up temporary files.
 * 
 * @param sessionId - The upload session ID to cancel
 * @returns Promise that resolves when cancellation is complete
 */
export async function cancelChunkedUpload(sessionId: string): Promise<{ message: string }> {
  try {
    // Get authentication token
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {};
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(getApiUrl(`/video/chunked/session/${sessionId}`), {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to cancel chunked upload: ${response.status}`);
    }

    const data = await response.json();
    console.log("Chunked upload cancelled:", data);
    return data;
  } catch (error) {
    console.error('Error cancelling chunked upload:', error);
    throw error;
  }
}

/**
 * Helper function to determine if a file should use chunked upload.
 * Files >= 1GB should use chunked upload for better reliability.
 * 
 * @param fileSize - Size of the file in bytes
 * @returns boolean indicating if chunked upload should be used
 */
export function shouldUseChunkedUpload(fileSize: number): boolean {
  const CHUNKED_UPLOAD_THRESHOLD = 1024 * 1024 * 1024; // 1GB in bytes
  return fileSize >= CHUNKED_UPLOAD_THRESHOLD;
}

/**
 * Helper function to calculate the number of chunks needed for a file.
 * Uses 50MB chunks as recommended by your backend.
 * 
 * @param fileSize - Size of the file in bytes
 * @returns number of chunks needed
 */
export function calculateChunkCount(fileSize: number): number {
  const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  return Math.ceil(fileSize / CHUNK_SIZE);
}

/**
 * Helper function to get a specific chunk from a file.
 * 
 * @param file - The original file
 * @param chunkIndex - Zero-based index of the chunk to extract
 * @param chunkSize - Size of each chunk (default 50MB)
 * @returns Blob containing the chunk data
 */
export function getFileChunk(file: File, chunkIndex: number, chunkSize: number = 50 * 1024 * 1024): Blob {
  const start = chunkIndex * chunkSize;
  const end = Math.min(start + chunkSize, file.size);
  return file.slice(start, end);
}
  
// ============================================================================
// CLIENT-SIDE OPTIMIZATION FUNCTIONS - Pre-upload Compression
// ============================================================================

/**
 * Check if browser supports client-side video processing
 */
export function supportsClientSideProcessing(): boolean {
  return !!(
    window.HTMLCanvasElement &&
    window.MediaRecorder &&
    window.URL &&
    window.URL.createObjectURL
  );
}

/**
 * Optimize video file before upload using canvas compression
 */
export async function optimizeVideoForUpload(
  file: File, 
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    targetSizeMB?: number;
    onProgress?: (progress: number) => void;
  } = {}
): Promise<{ optimizedFile: File; compressionRatio: number; timeSaved: string }> {
  const {
    maxWidth = 1280,  // Reduced from 1920 for better compression
    maxHeight = 720,  // Reduced from 1080 for better compression
    quality = 0.6,    // Reduced from 0.8 for better compression
    targetSizeMB = 1024,
    onProgress
  } = options;

  console.log(`🔧 Starting client-side video optimization...`);
  console.log(`📊 Original file: ${file.name}, ${(file.size / (1024 * 1024)).toFixed(2)} MB`);

  onProgress?.(10);

  try {
    // Check if we can actually compress this file
    if (file.size < 50 * 1024 * 1024) { // Less than 50MB
      console.log('🔄 File is already small, skipping optimization');
      onProgress?.(100);
      return {
        optimizedFile: file,
        compressionRatio: 1,
        timeSaved: '0 minutes'
      };
    }

    // Create video element for processing
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    
    onProgress?.(20);
    
    // Load video
    await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Video loading timed out'));
      }, 15000); // 15 second timeout
      
      video.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        resolve(undefined);
      };
      video.onerror = (e) => {
        clearTimeout(timeoutId);
        reject(new Error('Failed to load video for optimization'));
      };
      video.src = URL.createObjectURL(file);
    });

    onProgress?.(30);

    const { videoWidth, videoHeight, duration } = video;
    console.log(`📐 Original: ${videoWidth}x${videoHeight}, Duration: ${duration}s`);
    
    // Calculate new dimensions
    const scale = Math.min(maxWidth / videoWidth, maxHeight / videoHeight, 1);
    const newWidth = Math.floor(videoWidth * scale);
    const newHeight = Math.floor(videoHeight * scale);
    
    console.log(`📏 New dimensions: ${newWidth}x${newHeight} (scale: ${scale.toFixed(2)})`);
    
    onProgress?.(40);

    // Create canvas for rendering
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    onProgress?.(50);

    // Check if MediaRecorder is supported with the codec we want
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8') 
      ? 'video/webm;codecs=vp8' 
      : MediaRecorder.isTypeSupported('video/webm') 
        ? 'video/webm' 
        : 'video/mp4';
    
    console.log(`🎥 Using MIME type: ${mimeType}`);

    // Set up MediaRecorder with aggressive compression
    const stream = canvas.captureStream(24); // 24 FPS
    
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      throw new Error(`MediaRecorder does not support ${mimeType}`);
    }
    
    // Calculate a reasonable bitrate based on resolution
    const targetBitrate = Math.min(
      newWidth * newHeight * 0.1, // Very low bitrate for compression
      500000 // Max 500kbps
    );
    
    console.log(`🎯 Target bitrate: ${targetBitrate} bps`);
    
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: mimeType,
      videoBitsPerSecond: targetBitrate
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    onProgress?.(60);

    // Start recording
    mediaRecorder.start(1000); // Record in 1-second chunks
    
    let frameCount = 0;
    const totalFrames = duration * 24; // Approximate frames at 24fps
    
    // Play video and capture frames
    const processVideo = new Promise<void>((resolve, reject) => {
      const captureFrame = () => {
        if (video.ended || video.currentTime >= duration) {
          mediaRecorder.stop();
          resolve();
          return;
        }
        
        // Draw current frame to canvas
        ctx.drawImage(video, 0, 0, newWidth, newHeight);
        
        frameCount++;
        const progress = Math.min(60 + (frameCount / totalFrames) * 30, 90);
        onProgress?.(progress);
        
        // Continue to next frame
        requestAnimationFrame(captureFrame);
      };
      
      video.ontimeupdate = captureFrame;
      video.onended = () => {
        mediaRecorder.stop();
        resolve();
      };
      video.onerror = reject;
      
      video.play().catch(reject);
    });

    // Wait for video processing to complete
    await processVideo;
    
    onProgress?.(90);

    // Wait for MediaRecorder to finish
    const optimizedBlob = await new Promise<Blob>((resolve) => {
      mediaRecorder.onstop = () => {
        console.log(`📹 MediaRecorder stopped, collected ${chunks.length} chunks`);
        const blob = new Blob(chunks, { type: mimeType });
        console.log(`📦 Created blob with size: ${(blob.size / (1024 * 1024)).toFixed(2)} MB`);
        resolve(blob);
      };
    });

    // Create optimized file
    const optimizedFile = new File([optimizedBlob], 
      file.name.replace(/\.[^/.]+$/, mimeType.includes('webm') ? '.webm' : '.mp4'), 
      { type: mimeType }
    );

    onProgress?.(95);

    const compressionRatio = file.size / optimizedFile.size;
    const timeSavedMinutes = Math.round((file.size - optimizedFile.size) / (1024 * 1024) * 0.3);

    onProgress?.(100);

    console.log(`🎉 Optimization complete!`);
    console.log(`📉 Size: ${(file.size / (1024 * 1024)).toFixed(2)} MB → ${(optimizedFile.size / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`📊 Compression ratio: ${compressionRatio.toFixed(2)}x`);

    // Clean up
    URL.revokeObjectURL(video.src);

    return {
      optimizedFile,
      compressionRatio,
      timeSaved: `${Math.max(timeSavedMinutes, 1)} minutes`
    };

  } catch (error) {
    console.error('❌ Client-side optimization failed:', error);
    onProgress?.(0);
    
    // Return original file if optimization fails
    return {
      optimizedFile: file,
      compressionRatio: 1,
      timeSaved: '0 minutes'
    };
  }
}

/**
 * Show optimization options to user
 */
export function getOptimizationRecommendation(fileSize: number): {
  shouldOptimize: boolean;
  estimatedSavings: string;
  estimatedTimeSaved: string;
} {
  const fileSizeMB = fileSize / (1024 * 1024);
  const fileSizeGB = fileSize / (1024 * 1024 * 1024);

  if (fileSizeMB < 100) {
    return {
      shouldOptimize: false,
      estimatedSavings: 'No optimization needed',
      estimatedTimeSaved: '0 minutes'
    };
  }

  const estimatedCompressionRatio = 3; // Conservative estimate
  const estimatedNewSize = fileSizeMB / estimatedCompressionRatio;
  const timeSavedMinutes = Math.round((fileSizeMB - estimatedNewSize) * 0.5);

  return {
    shouldOptimize: true,
    estimatedSavings: `${fileSizeMB.toFixed(0)} MB → ${estimatedNewSize.toFixed(0)} MB`,
    estimatedTimeSaved: `~${timeSavedMinutes} minutes faster upload`
  };
}
